import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CHIVO, Wordmark } from "@/components/site/Chrome";

/* Shared score-report primitives — the hero card and the product score report render the same data shape:
   { score_pct, correct, total, questions_per_exam, pass_mark_pct, domains:[{id,name,pct,correct,total}], weakest, callout } */

export function DomainRows({ report, compact = false, testid = "domain-rows" }) {
  const cols = compact ? "minmax(0,1fr) 96px 50px" : "minmax(0,1fr) 200px 90px";
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column" }}>
      {report.domains.map((d, i) => {
        const weak = d.name === report.weakest;
        return (
          <div key={d.id || d.name} style={{ display: "grid", gridTemplateColumns: cols, gap: compact ? 16 : 20, alignItems: "center", padding: compact ? "11px 0" : "15px 0",
                                              borderTop: `1px solid ${compact ? "#F1F5F9" : "var(--hair)"}`, borderBottom: !compact && i === report.domains.length - 1 ? "1px solid var(--hair)" : undefined,
                                              background: !compact && weak ? "var(--surface)" : undefined }}>
            <p style={{ fontSize: compact ? 13.5 : 15, whiteSpace: compact ? "nowrap" : undefined, overflow: "hidden", textOverflow: "ellipsis", fontWeight: !compact && weak ? 600 : 400 }}>
              <span className="mono" style={{ color: "var(--ink-6)", marginRight: compact ? 9 : 10, fontWeight: 400 }}>{d.id}</span>{d.name}
              {!compact && weak && <span className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: "var(--ac-deep)", marginLeft: 12 }}>WEAKEST</span>}
            </p>
            <div className={`bar${compact ? " round" : ""}`} style={{ height: compact ? 6 : 8 }}><i className={weak ? "weak" : ""} style={{ width: `${Math.round(d.pct)}%` }} /></div>
            <p className="mono" style={{ fontSize: compact ? 13 : 14, textAlign: "right" }}>{compact ? `${Math.round(d.pct)}%` : `${d.correct}/${d.total} · ${Math.round(d.pct)}%`}</p>
          </div>
        );
      })}
    </div>
  );
}

export function useSampleReport() {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/site/sample-report").then((r) => setRep(r.data)).catch(() => setRep({ available: false })); }, []);
  return rep;
}

export function HeroScoreCard({ mob }) {
  const rep = useSampleReport();
  const card = { background: "#FFFFFF", borderRadius: 16, position: "relative", zIndex: 3, overflow: "hidden",
                 boxShadow: "0 2px 4px rgba(9,42,66,0.05), 0 34px 70px -26px rgba(9,42,66,0.35)", ...(mob ? {} : { margin: "0 0 -96px 0" }) };
  const above = rep?.available && rep.score_pct >= rep.pass_mark_pct;
  return (
    <div style={card} data-testid="hero-score-card">
      <div style={{ padding: "18px 28px", borderBottom: "1px solid #EEF3F8", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <Wordmark size={16} img={24} testid="hero-card-logo" />
        <span className="label" data-testid="hero-score-label">LIVE SCORE COMPONENT · {rep?.available ? (rep.sample ? "SAMPLE DATA" : "LATEST REPORT") : "LOADING"}</span>
      </div>
      {!rep && <div style={{ padding: 28, minHeight: 320 }} data-testid="hero-score-loading"><p className="label">LOADING…</p></div>}
      {rep && !rep.available && (
        <div style={{ padding: 28, minHeight: 320 }} data-testid="hero-score-empty">
          <p className="label">SCORE</p>
          <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.6, color: "var(--ink-3)" }}>Publish a certification with a banked exam and the sample report renders here automatically.</p>
        </div>
      )}
      {rep?.available && (
        <>
          <div style={{ padding: 28 }}>
            <p className="label" data-testid="hero-score-kicker">SCORE · <span className="dash">{rep.cert_short.toUpperCase()}</span> <span className="dash">EXAM {rep.exam_index}</span> OF <span className="dash">{rep.exams_per_cert}</span></p>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
              <p data-testid="hero-score-pct" style={{ marginTop: 6, fontFamily: CHIVO, fontWeight: 900, letterSpacing: "-0.05em", lineHeight: 0.82, fontSize: mob ? 84 : 132 }}>{rep.score_pct}%</p>
              <div style={{ paddingBottom: 14 }}>
                <p style={{ fontSize: 14, color: "var(--ink-4)" }}>{rep.correct} of <span className="dash">{rep.total}</span> correct · pass mark <span className="dash">{rep.pass_mark_pct}</span>%</p>
                <p className="mono" style={{ marginTop: 8, fontSize: 10.5, letterSpacing: "0.12em", color: above ? "var(--ac-deep)" : "var(--ink)", background: above ? "var(--ac-tint)" : "var(--hair-2)", borderRadius: 99, padding: "6px 14px", display: "inline-block" }}>{above ? "ABOVE PASS MARK" : "BELOW PASS MARK"}</p>
              </div>
            </div>
            <div style={{ marginTop: 24 }}>
              <p className="label" style={{ marginBottom: 4 }}>BY DOMAIN</p>
              <DomainRows report={rep} compact testid="hero-domain-rows" />
            </div>
            {rep.callout && (
              <div style={{ marginTop: 20, borderRadius: 10, background: "linear-gradient(120deg,var(--ac-tint),var(--wash))", padding: "14px 16px" }} data-testid="hero-callout">
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-3)" }}><span style={{ fontWeight: 600 }}>{rep.callout.lead}</span> {rep.callout.body}</p>
              </div>
            )}
          </div>
          <div style={{ padding: "14px 28px", background: "linear-gradient(180deg,#FFFFFF,var(--wash))", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span className="label">{rep.missed} MISSED · EVERY ANSWER EXPLAINED</span>
            <span className="label" style={{ color: "var(--ac-deep)" }}>REVIEW →</span>
          </div>
        </>
      )}
    </div>
  );
}
